import React, { useState, useEffect, useContext, useMemo } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import Header from "../../../layout/Header/Header";
import { Button, ButtonGroup, Row, Table, Input, Label, Col} from "reactstrap";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import ExportJsonExcel from "js-export-excel";
import axios from "axios";
import { TableHeader, Pagination, Search } from "../../../DataTable";
import useFullPageLoader from "../../../hooks/useFullPageLoader";

function ReporteCompras() {
  const { user } = useContext(AuthContext);
  const URL_COMPRAS_ACUMULADOS = process.env.REACT_APP_URL_COMPRAS_ACUMULADOS;

  const [comments, setComments] = useState([]);
  const [proveedores, setProveedores] = useState([]);
  const [articulos, setArticulos] = useState([]);
  const [lineas, setLineas] = useState([]);
  const [marcas, setMarcas] = useState([]);
  const [familias, setFamilias] = useState([]);
  const [unidadesNegocio, setUnidadesNegocio] = useState([]);

  const [selectedProveedor, setSelectedProveedor] = useState("");
  const [selectedArticulo, setSelectedArticulo] = useState("");
  const [selectedLinea, setSelectedLinea] = useState("");
  const [selectedMarca, setSelectedMarca] = useState("");
  const [selectedFamilia, setSelectedFamilia] = useState("");
  const [selectedUnidadNegocio, setSelectedUnidadNegocio] = useState("");
  const [selectedFechaInicio, setSelectedFechaInicio] = useState("");
  const [selectedFechaFin, setSelectedFechaFin] = useState("");

  const [totalFOB, setTotalFOB] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState({ field: "", order: "" });
  const [loader, showLoader, hideLoader] = useFullPageLoader();

  const ITEMS_PER_PAGE = 50;

  useEffect(() => {
    showLoader();
    axios
      .get(`${URL_COMPRAS_ACUMULADOS}/reporte`, {
        headers: {
          Authorization: `Bearer: ${localStorage.getItem("app_token")}`,
        },
      })
      .then((res) => {
        const data = res.data || [];
        setComments(data);

        // Derivar catálogos únicos para los filtros
        const uniq = (arr, idKey, nameKey) => {
          const map = {};
          arr.forEach((a) => {
            if (a[idKey] && a[idKey] !== "NA") map[a[idKey]] = a[nameKey];
          });
          return Object.keys(map)
            .map((id) => ({ _id: id, name: map[id] }))
            .sort((x, y) => (x.name > y.name ? 1 : -1));
        };
        setProveedores(uniq(data, "idProveedor", "proveedor"));
        setArticulos(
          uniq(
            data.map((a) => ({ ...a, artName: `${a.SKU} ${a.nombre}` })),
            "idArticulo",
            "artName"
          )
        );
        setLineas(uniq(data, "idLinea", "linea"));
        setMarcas(uniq(data, "idMarca", "marca"));
        setFamilias(uniq(data, "idFamilia", "familia"));
        setUnidadesNegocio(uniq(data, "idUnidadNegocio", "unidadNegocio"));
        hideLoader();
      })
      .catch((err) => {
        console.log(err);
        hideLoader();
      });
  }, []);

  function limpiarSearch() {
    setSearch("");
    setSelectedProveedor("");
    setSelectedArticulo("");
    setSelectedLinea("");
    setSelectedMarca("");
    setSelectedFamilia("");
    setSelectedUnidadNegocio("");
    setSelectedFechaInicio("");
    setSelectedFechaFin("");
    setCurrentPage(1);
  }

  const headers = [
    { name: "Compra", field: "idCompra", sortable: true },
    { name: "Fecha", field: "fecha", sortable: true },
    { name: "SKU", field: "SKU", sortable: true },
    { name: "Nombre", field: "nombre", sortable: true },
    { name: "Proveedor", field: "proveedor", sortable: true },
    { name: "Cantidad", field: "cantidad", sortable: true },
    { name: "FOB", field: "FOB", sortable: true },
  ];

  // Aplica todos los filtros (menos paginación/orden). Reutilizable para PDF/Excel.
  const aplicarFiltros = (data) => {
    let out = data;
    if (search) {
      const s = search.toLowerCase();
      out = out.filter(
        (c) =>
          (c.SKU || "").toLowerCase().includes(s) ||
          (c.nombre || "").toLowerCase().includes(s) ||
          (c.proveedor || "").toLowerCase().includes(s) ||
          (c.fecha || "").toLowerCase().includes(s) ||
          c.idCompra.toString().includes(search) ||
          c.FOB.toString().includes(search)
      );
    }
    if (selectedProveedor)
      out = out.filter((e) => e.idProveedor === selectedProveedor);
    if (selectedArticulo)
      out = out.filter((e) => e.idArticulo === selectedArticulo);
    if (selectedLinea) out = out.filter((e) => e.idLinea === selectedLinea);
    if (selectedMarca) out = out.filter((e) => e.idMarca === selectedMarca);
    if (selectedFamilia)
      out = out.filter((e) => e.idFamilia === selectedFamilia);
    if (selectedUnidadNegocio)
      out = out.filter((e) => e.idUnidadNegocio === selectedUnidadNegocio);
    if (selectedFechaInicio)
      out = out.filter((e) => e.fecha >= selectedFechaInicio);
    if (selectedFechaFin) out = out.filter((e) => e.fecha <= selectedFechaFin);
    return out;
  };

  const commentsData = useMemo(() => {
    let computed = aplicarFiltros(comments);

    setTotalItems(computed.length);
    const tFOB = computed.reduce((t, c) => t + parseFloat(c.FOB || 0), 0);
    setTotalFOB(tFOB);

    const numericos = ["idCompra", "cantidad", "FOB"];
    if (sorting.field && !numericos.includes(sorting.field)) {
      const reversed = sorting.order === "asc" ? 1 : -1;
      computed = [...computed].sort(
        (a, b) =>
          reversed * (a[sorting.field] || "").localeCompare(b[sorting.field] || "")
      );
    }
    if (sorting.field && numericos.includes(sorting.field)) {
      const reversed = sorting.order === "asc" ? 1 : -1;
      computed = [...computed].sort(
        (a, b) =>
          reversed *
          (parseFloat(a[sorting.field] || 0) - parseFloat(b[sorting.field] || 0))
      );
    }

    return computed.slice(
      (currentPage - 1) * ITEMS_PER_PAGE,
      (currentPage - 1) * ITEMS_PER_PAGE + ITEMS_PER_PAGE
    );
  }, [
    comments,
    currentPage,
    search,
    sorting,
    selectedProveedor,
    selectedArticulo,
    selectedLinea,
    selectedMarca,
    selectedFamilia,
    selectedUnidadNegocio,
    selectedFechaInicio,
    selectedFechaFin,
  ]);

  function PDFTabla() {
    const filtrados = aplicarFiltros(comments);
    let tFOB = 0;
    const body = filtrados.map((a) => {
      tFOB += parseFloat(a.FOB || 0);
      return [
        a.idCompra,
        a.fecha,
        a.SKU,
        a.nombre,
        a.proveedor,
        new Intl.NumberFormat("en-US").format(a.cantidad),
        "$" +
          new Intl.NumberFormat("en-US", {
            minimumFractionDigits: 2,
          }).format(a.FOB),
      ];
    });
    const doc = new jsPDF("landscape", "mm", "a4", true);
    var img = new Image();
    img.src = process.env.PUBLIC_URL + "Logo-Lynqro-PDF.png";
    doc.addImage(img, "png", 230, 12, 45, 15);
    var img2 = new Image();
    img2.src = process.env.PUBLIC_URL + "logoInstitucion.png";
    doc.addImage(img2, "png", 15, 10, 40, 12);
    doc.text(`Reporte de Compras`, 120, 20);
    doc.autoTable({
      head: [
        ["Compra", "Fecha", "SKU", "Nombre", "Proveedor", "Cantidad", "FOB"],
      ],
      body: body,
      startY: 30,
      foot: [
        [
          "",
          "",
          "",
          "",
          "TOTAL FOB",
          "",
          "$" +
            new Intl.NumberFormat("en-US", {
              minimumFractionDigits: 2,
            }).format(tFOB),
        ],
      ],
      showFoot: "lastPage",
    });
    doc.save(`ReporteCompras.pdf`);
  }

  function excel() {
    const filtrados = aplicarFiltros(comments);
    const dataExcel = filtrados.map((a) => ({
      Compra: a.idCompra,
      Fecha: a.fecha,
      SKU: a.SKU,
      Nombre: a.nombre,
      Proveedor: a.proveedor,
      Linea: a.linea,
      Marca: a.marca,
      Familia: a.familia,
      UnidadNegocio: a.unidadNegocio,
      Cantidad: a.cantidad,
      FOB: a.FOB,
    }));
    const option = {
      fileName: "ReporteCompras",
      datas: [
        {
          sheetData: dataExcel,
          sheetName: "Compras",
          sheetHeader: [
            "Compra",
            "Fecha",
            "SKU",
            "Nombre",
            "Proveedor",
            "Linea",
            "Marca",
            "Familia",
            "UnidadNegocio",
            "Cantidad",
            "FOB",
          ],
        },
      ],
    };
    var toExcel = new ExportJsonExcel(option);
    toExcel.saveExcel();
  }

  return (
    <>
      <Header />
      <br />
      <br />
      {user.menu_compras ? (
        <div className="card col-12">
          <Row>
            <Col md={8}>
              <Button
                size="sm"
                href="/MenuCompras"
                className="btn btn-danger"
                id="botonListado"
              >
                Regresar
              </Button>
            </Col>
            <Col md={4} align="right">
            <ButtonGroup>
              <Button size="sm" color="danger" onClick={PDFTabla}>
                PDF <i class="far fa-file-pdf"></i>
              </Button>
              <Button size="sm" color="primary" onClick={excel}>
                Excel <i class="far fa-file-excel"></i>
              </Button>
            </ButtonGroup>
            </Col>
          </Row>
          <h3 align="center">Reporte de Compras</h3>
          <div className="row">
            <div className="col-md-6">
              <Pagination
                total={totalItems}
                itemsPerPage={ITEMS_PER_PAGE}
                currentPage={currentPage}
                onPageChange={(page) => setCurrentPage(page)}
              />
            </div>
            <div className="col-md-4 d-flex flex-row-reverse">
              <Search
                onSearch={(value) => {
                  setSearch(value);
                  setCurrentPage(1);
                }}
              />
            </div>
            <div className="col-md-2 flex-row-reverse">
              <Button size="sm" className="btn" color="info" onClick={limpiarSearch}>
                Limpiar
              </Button>
            </div>
          </div>
          <Row>
            <Col md={2}>
              <Label>Fecha Inicio</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaInicio}
                onChange={(e) => {
                  setSelectedFechaInicio(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </Col>
            <Col md={2}>
              <Label>Fecha Fin</Label>
              <Input
                bsSize="sm"
                type="date"
                value={selectedFechaFin}
                onChange={(e) => {
                  setSelectedFechaFin(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </Col>
            <Col md={4}>
              <Label>Proveedor</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedProveedor}
                onChange={(e) => {
                  setSelectedProveedor(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Todos</option>
                {proveedores.map((a) => (
                  <option key={a._id} value={a._id}>
                    {a.name}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={4}>
              <Label>Artículo (SKU)</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedArticulo}
                onChange={(e) => {
                  setSelectedArticulo(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Todos</option>
                {articulos.map((a) => (
                  <option key={a._id} value={a._id}>
                    {a.name}
                  </option>
                ))}
              </Input>
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <Label>Línea</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedLinea}
                onChange={(e) => {
                  setSelectedLinea(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Todas</option>
                {lineas.map((a) => (
                  <option key={a._id} value={a._id}>
                    {a.name}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={3}>
              <Label>Marca</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedMarca}
                onChange={(e) => {
                  setSelectedMarca(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Todas</option>
                {marcas.map((a) => (
                  <option key={a._id} value={a._id}>
                    {a.name}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={3}>
              <Label>Familia</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedFamilia}
                onChange={(e) => {
                  setSelectedFamilia(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Todas</option>
                {familias.map((a) => (
                  <option key={a._id} value={a._id}>
                    {a.name}
                  </option>
                ))}
              </Input>
            </Col>
            <Col md={3}>
              <Label>Unidad Negocio</Label>
              <Input
                bsSize="sm"
                type="select"
                value={selectedUnidadNegocio}
                onChange={(e) => {
                  setSelectedUnidadNegocio(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Todas</option>
                {unidadesNegocio.map((a) => (
                  <option key={a._id} value={a._id}>
                    {a.name}
                  </option>
                ))}
              </Input>
            </Col>
          </Row>
          <br />
          <Table size="sm" striped borderless className="table-responsive-xl">
            <TableHeader
              headers={headers}
              onSorting={(field, order) => setSorting({ field, order })}
            />
            <tbody>
              {commentsData.map((a) => (
                <tr key={a.id}>
                  <td>{a.idCompra}</td>
                  <td>{a.fecha}</td>
                  <td>{a.SKU}</td>
                  <td>{a.nombre}</td>
                  <td>{a.proveedor}</td>
                  <td>{new Intl.NumberFormat("en-US").format(a.cantidad)}</td>
                  <td>
                    {"$" +
                      new Intl.NumberFormat("en-US", {
                        minimumFractionDigits: 2,
                      }).format(a.FOB)}
                  </td>
                </tr>
              ))}
              {/* <tr>
                <td></td>
                <td></td>
                <td></td>
                <td></td>
                <td className="negrita">TOTAL FOB</td>
                <td></td>
                <td className="negrita">
                  {"$" +
                    new Intl.NumberFormat("en-US", {
                      minimumFractionDigits: 2,
                    }).format(totalFOB)}
                </td>
              </tr> */}
            </tbody> 
          </Table>
          <div className="col-md-6">
            <Pagination
              total={totalItems}
              itemsPerPage={ITEMS_PER_PAGE}
              currentPage={currentPage}
              onPageChange={(page) => setCurrentPage(page)}
            />
          </div>
        </div>
      ) : undefined}
      {loader}
    </>
  );
}

export default ReporteCompras;
